import { useEffect, useState, useCallback  } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { RefreshCw, AlertTriangle, Trash2, Loader2, BadgeCheck, Building2, Clock, CheckCircle2 } from "lucide-react";
import { Shield, KeyRound } from "lucide-react";
import { apiFetch, ensureAuthOrRedirect,  redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";

// Denser SOC/Datadog-style panel — flat dark surface, hairline sky border,
// no heavy glass/shadow.
const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

// Compact section header — matches the InfectionDashboard pattern: small
// uppercase eyebrow + title + optional description.
function SectionHeader({ index, title, description, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">
          {String(index ?? "").padStart(2, "0")} · {title}
        </div>
        {description && (
          <div className="text-[12px] text-neutral-500">{description}</div>
        )}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

// Compact field shell used in inline forms. Keeps inputs tight and aligned.
const inputClass =
  "w-full rounded-md border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors";

function Banner({ type, message, onDismiss }) {
  if (!message) return null;
  return (
    <div
      className={`mb-4 rounded-lg px-4 py-3 text-sm flex justify-between items-center ${
        type === "success"
          ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
          : "bg-red-500/10 border border-red-500/40 text-red-300"
      }`}
    >
      <span>{message}</span>
      <button
        onClick={onDismiss}
        className="ml-3 text-xs underline hover:text-white"
      >
        Dismiss
      </button>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
const [showKey, setShowKey] = useState(false);
  const [user, setUser] = useState(null);
  const [logins, setLogins] = useState([]);
  const [apiInfo, setApiInfo] = useState([]);
  const [expiryWarning, setExpiryWarning] = useState(null);

  const [activeTab, setActiveTab] = useState("subscription");
  const [confirmOpen, setConfirmOpen] = useState(false);
const [regenLoading, setRegenLoading] = useState(false);
const [apiNotice, setApiNotice] = useState(null);
const [apiError, setApiError] = useState(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [mfaPassword, setMfaPassword] = useState("");

  const [banner, setBanner] = useState({ type: null, message: "" });

  // Delete-account modal state. Precheck is fetched lazily when the modal
  // opens, since it carries org/admin info that's not useful on initial load.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePrecheck, setDeletePrecheck] = useState(null);
  const [deletePrecheckLoading, setDeletePrecheckLoading] = useState(false);
  const [deletePrecheckError, setDeletePrecheckError] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");

  const token =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("jwtToken") ||
    window.WHITEINTEL_TOKEN ||
    "";

 useEffect(() => {
  fetch("https://whiteintel.io/api/regular/app/v2/user/get_user_information.php", {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => {
      // 🔐 handle auth errors before parsing
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      return res.json();
    })
    .then((data) => {
      setUser(data.user);
      setTwoFAEnabled(data.user["2fa"] === 1);
      setLogins(data.logins || []);
      setApiInfo(data?.api ?? null); // not [] or {}
      if (data.user?.subscription_expires_at) {
        const today = new Date();
        const expiryDate = new Date(data.user.subscription_expires_at);
        const diffDays = Math.ceil(
          (expiryDate - today) / (1000 * 60 * 60 * 24)
        );

        if (diffDays < 0) {
          setExpiryWarning("Your subscription has expired.");
        } else if (diffDays <= 90) {
          setExpiryWarning(`Your subscription expires in ${diffDays} days.`);
        }
      }
    })
    .catch((err) => {
      if (!err.message.startsWith("AUTH_REDIRECT_")) {
        setBanner({
          type: "error",
          message: "Failed to load profile data.",
        });
      }
    });
}, []);


  const handleUpgrade = () => {
    try {
      window.location.href = "/upgrades";
    } catch {
      window.open("mailto:sales@whiteintel.io", "_blank");
    }
  };

const handleRegenerateKey = useCallback(async () => {
  try {
    setRegenLoading(true);
    setApiNotice(null);
    setApiError(null);

    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/apikeys/regenerate_api.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      }
    );

    const data = await res.json(); // { apikey: "newapikey" } or { error: "..." }
    if (!res.ok || !data?.apikey) {
      throw new Error(data?.error || "Failed to regenerate key");
    }

    // update apiInfo in place
    setApiInfo((prev) => ({ ...(prev || {}), api_key: data.apikey }));
    setShowKey(false);
    setApiNotice("API key regenerated successfully.");
  } catch (e) {
    setApiError(e.message || "Failed to regenerate key");
  } finally {
    setRegenLoading(false);
    setConfirmOpen(false);
  }
}, []);

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !retypePassword) {
      setBanner({ type: "error", message: "All fields are required." });
      return;
    }
    if (newPassword !== retypePassword) {
      setBanner({ type: "error", message: "New passwords do not match." });
      return;
    }

    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/user/update_password.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: currentPassword,
            newPassword,
            newPasswordReType: retypePassword,
          }),
        }
      );
      const data = await res.json();

      if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }
      if (res.ok) {
        setBanner({ type: "success", message: "Password updated successfully." });
        setCurrentPassword("");
        setNewPassword("");
        setRetypePassword("");
      } else {
        setBanner({ type: "error", message: data?.error || "Failed to update password." });
      }
    } catch {
      setBanner({ type: "error", message: "Network error while updating password." });
    }
  };

  // Fetch when the delete modal opens. Tells us whether this user is the
  // sole admin of an org (in which case they must nominate a successor first)
  // and gives us the user list to pick from.
  useEffect(() => {
    if (!deleteOpen) return;
    let cancelled = false;
    (async () => {
      setDeletePrecheckLoading(true);
      setDeletePrecheckError("");
      setDeletePrecheck(null);
      try {
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/user/delete_account_precheck.php",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        if (!cancelled) setDeletePrecheck(data);
      } catch (e) {
        if (!cancelled) setDeletePrecheckError(e.message || "Failed to load account info.");
      } finally {
        if (!cancelled) setDeletePrecheckLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deleteOpen, token]);

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setDeletePassword("");
    setDeleteConfirmText("");
    setDeleteError("");
    setDeleteSuccess("");
    setDeletePrecheck(null);
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    setDeleteLoading(true);
    try {
      const payload = { password: deletePassword };
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/user/delete_account.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));

      // success===true is the only signal we trust as "the delete happened".
      // Anything else — including 401/403/404/500 with `success: false` —
      // means the account was NOT deleted, so surface the server's error in
      // the modal instead of redirecting away. The user needs to see why.
      if (data?.success !== true) {
        throw new Error(data?.error || `Failed to delete account (HTTP ${res.status})`);
      }

      setDeleteSuccess(data?.message || "Your account has been deactivated.");

      // Clear all session tokens so the next nav can't reuse stale auth.
      try {
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("token");
        sessionStorage.removeItem("jwtToken");
      } catch {}

      // Brief pause so the user sees the success state, then bounce to login.
      setTimeout(() => {
        window.location.href = "/login?deleted=1";
      }, 1500);
    } catch (e) {
      setDeleteError(e.message || "Failed to delete account.");
      setDeleteLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    if (!mfaPassword) {
      setBanner({
        type: "error",
        message: "Enter your current password to change 2FA.",
      });
      return;
    }

    try {
      const action = twoFAEnabled ? "disable" : "enable";
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/user/mfa_handler.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: mfaPassword,
            action,
          }),
        }
      );
      const data = await res.json();

        if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }

      if (res.ok) {
        setTwoFAEnabled(!twoFAEnabled);
        setBanner({
          type: "success",
          message: `Two-Factor Authentication ${action}d successfully.`,
        });
      } else {
        setBanner({ type: "error", message: data?.error || "Failed to update 2FA." });
      }
    } catch {
      setBanner({ type: "error", message: "Network error while updating 2FA." });
    }
  };

  // ---- derived ----
  let daysLeft = null;
  if (user?.subscription_expires_at) {
    const today = new Date();
    const exp = new Date(user.subscription_expires_at);
    daysLeft = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  }
  const expired = daysLeft !== null && daysLeft < 0;
  const expiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Profile settings
            </h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pt-4 pb-10">

          {/* Banner for feedback */}
          {banner.message && (
            <Banner
              type={banner.type}
              message={banner.message}
              onDismiss={() => setBanner({ type: null, message: "" })}
            />
          )}

          {/* ============ IDENTITY STRIP ============
              Compact header strip with avatar/initial, email/org, and
              status chips. Replaces the previous bare page title. */}
          {user && (
            <div className={`${panel} px-5 py-4 flex flex-wrap items-center gap-4`}>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-white truncate">{user.email}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-neutral-500 font-mono uppercase tracking-wider">
                  <Building2 className="h-3 w-3" />
                  <span className="truncate">{user.organization_name || "Personal account"}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-sky-300">
                  <BadgeCheck className="h-3 w-3" />
                  {user.subscription || "Free"}
                </span>
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider ${
                  twoFAEnabled
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-300"
                }`}>
                  <Shield className="h-3 w-3" />
                  2FA {twoFAEnabled ? "On" : "Off"}
                </span>
                {daysLeft !== null && (
                  <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider ${
                    expired
                      ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                      : expiringSoon
                      ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                      : "border-neutral-700 bg-neutral-900/50 text-neutral-300"
                  }`}>
                    <Clock className="h-3 w-3" />
                    {expired ? "Expired" : `${daysLeft}d left`}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 lg:items-stretch">
            {/* LEFT COLUMN — Password + 2FA, each stretches to fill half the
                column height so the two cards together match the right card. */}
            <div className="flex flex-col gap-4 lg:h-[78vh]">

              {/* Update Password */}
              <section className={`${panel} p-5 flex-1 overflow-auto`}>
                <SectionHeader
                  index={1}
                  title="Password"
                  description="Use a strong, unique password — at least 12 characters."
                />
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                      Current password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                        New password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                        Confirm
                      </label>
                      <input
                        type="password"
                        value={retypePassword}
                        onChange={(e) => setRetypePassword(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end pt-1">
                    <button
                      onClick={handlePasswordUpdate}
                      className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white transition-colors"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      Update password
                    </button>
                  </div>
                </div>
              </section>

              {/* Two-Factor Authentication */}
              <section className={`${panel} p-5 flex-1 overflow-auto`}>
                <SectionHeader
                  index={2}
                  title="Two-factor authentication"
                  description="Email-delivered one-time codes on each sign in."
                  right={
                    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                      twoFAEnabled
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : "border-neutral-700 bg-neutral-900/50 text-neutral-400"
                    }`}>
                      <Shield className="h-3 w-3" />
                      {twoFAEnabled ? "Enabled" : "Disabled"}
                    </span>
                  }
                />
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                      Current password
                    </label>
                    <input
                      type="password"
                      value={mfaPassword}
                      onChange={(e) => setMfaPassword(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      onClick={handleToggle2FA}
                      className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                        twoFAEnabled
                          ? "border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                          : "bg-sky-600 hover:bg-sky-500 text-white"
                      }`}
                    >
                      <Shield className="h-3.5 w-3.5" />
                      {twoFAEnabled ? "Disable 2FA" : "Enable 2FA"}
                    </button>
                  </div>
                </div>
              </section>

            </div>

            {/* RIGHT COLUMN — same fixed height as the left column. */}
            <div className={`${panel} lg:h-[78vh] flex flex-col`}>
              {/* Underline tab strip — Datadog/Splunk style. */}
              <div className="flex items-stretch border-b border-sky-900/30 px-2">
                {[
                  { id: "subscription", label: "Account"  },
                  { id: "logins",       label: "Sessions" },
                  { id: "api",          label: "API key"  },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`relative px-3 py-3 text-[12px] font-semibold uppercase tracking-wider transition-colors ${
                      activeTab === t.id
                        ? "text-white"
                        : "text-neutral-500 hover:text-neutral-300"
                    }`}
                  >
                    {t.label}
                    {activeTab === t.id && (
                      <span className="absolute inset-x-2 -bottom-px h-0.5 bg-sky-400" />
                    )}
                  </button>
                ))}
              </div>

              {activeTab === "subscription" && user && (
                <div className="flex-1 overflow-auto p-5">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">Email</dt>
                      <dd className="text-white font-mono truncate" title={user.email}>{user.email}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">Organization</dt>
                      <dd className="text-white truncate" title={user.organization_name}>{user.organization_name || "Personal account"}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">Plan</dt>
                      <dd>
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-sky-300">
                          <BadgeCheck className="h-3 w-3" />
                          {user.subscription || "Free"}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">Expires</dt>
                      <dd className="text-white font-mono tabular-nums">{user.subscription_expires_at || "—"}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">Status</dt>
                      <dd>
                        {expired ? (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-rose-300">
                            <AlertTriangle className="h-3 w-3" />
                            Subscription expired
                          </span>
                        ) : expiringSoon ? (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-amber-300">
                            <Clock className="h-3 w-3" />
                            Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-emerald-300">
                            <BadgeCheck className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6 pt-4 border-t border-sky-900/20 flex items-center justify-between flex-wrap gap-3">
                    <p className="text-[12px] text-neutral-500">
                      Need more capacity, retention, or seats?
                    </p>
                    <button
                      onClick={handleUpgrade}
                      className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white transition-colors"
                    >
                      Manage plan
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "logins" && (
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-[12px]">
                    <thead className="sticky top-0 bg-[#0a1224]/95 backdrop-blur-sm">
                      <tr className="text-neutral-500 border-b border-sky-900/30">
                        <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Date</th>
                        <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">IP address</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sky-900/20">
                      {logins.map((log, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-2 text-neutral-300 font-mono tabular-nums">{log.login_date}</td>
                          <td className="px-4 py-2 text-neutral-300 font-mono">{log.ip}</td>
                        </tr>
                      ))}
                      {logins.length === 0 && (
                        <tr>
                          <td colSpan="2" className="px-4 py-8 text-center text-neutral-500 text-[12px]">No sign-ins on record yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "api" && (
                apiInfo ? (
                  <div className="flex-1 overflow-auto p-5 space-y-4">
                    {apiInfo.inherited_from_organization && (
                      <div className="rounded-md bg-amber-500/10 border border-amber-600/40 px-3 py-2 text-[12px] text-amber-200 flex items-start gap-2">
                        <Building2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          This API key is inherited from your organization:{" "}
                          <span className="font-semibold text-amber-100">{user?.organization_name || "Your Organization"}</span>
                        </span>
                      </div>
                    )}

                    {apiNotice && (
                      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
                        {apiNotice}
                      </div>
                    )}
                    {apiError && (
                      <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-200">
                        {apiError}
                      </div>
                    )}

                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1.5">Key</div>
                      <div className="flex items-center justify-between gap-2 bg-neutral-900/60 border border-sky-900/30 rounded-md px-3 py-2">
                        <span className="font-mono text-[12px] tracking-wider text-neutral-200 select-none break-all">
                          {showKey ? (apiInfo?.api_key || "—") : (apiInfo?.api_key?.replace(/.(?=.{4})/g, "•") || "—")}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => setShowKey(!showKey)}
                            className="rounded px-2 py-1 text-[10px] uppercase tracking-wider font-mono text-neutral-400 hover:text-white hover:bg-white/[0.06] transition"
                          >
                            {showKey ? "Hide" : "Show"}
                          </button>
                          <button
                            onClick={() => navigator.clipboard.writeText(apiInfo.api_key)}
                            className="rounded px-2 py-1 text-[10px] uppercase tracking-wider font-mono text-neutral-400 hover:text-white hover:bg-white/[0.06] transition"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    {!apiInfo.inherited_from_organization ? (
                      <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-sky-900/20">
                        <p className="text-[12px] text-neutral-500 max-w-md">
                          Rotating will immediately invalidate the old key. Update all integrations before regenerating.
                        </p>
                        <button
                          onClick={() => setConfirmOpen(true)}
                          disabled={regenLoading}
                          className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[12px] font-medium text-sky-200 transition disabled:opacity-60"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${regenLoading ? "animate-spin" : ""}`} />
                          {regenLoading ? "Regenerating…" : "Regenerate"}
                        </button>
                      </div>
                    ) : (
                      <p className="text-[12px] text-neutral-500 pt-2 border-t border-sky-900/20">
                        Keep this key secure. Only organization admins can rotate it.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto p-5">
                    <div className="rounded-md border border-amber-600/40 bg-amber-500/10 p-4 flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="text-[13px] font-semibold text-amber-100">No API key available</div>
                        <p className="text-[12px] text-amber-200/80 mt-1">
                          API access is not enabled for your plan. Upgrade to access the Whiteintel REST API.
                        </p>
                        <button
                          onClick={handleUpgrade}
                          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-[12px] font-semibold text-white transition-all"
                        >
                          Upgrade plan
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Regenerate confirmation modal */}
              {confirmOpen && (
                <div
                  className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                  onClick={() => setConfirmOpen(false)}
                >
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                  <div
                    className="relative z-[101] w-full max-w-md rounded-md border border-sky-900/40 bg-[#0a1224]/95 p-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="text-[14px] font-semibold">Regenerate API key?</h3>
                    <p className="mt-2 text-[13px] text-neutral-300">
                      This will immediately replace the current key. Clients using the old key will stop working until updated.
                    </p>
                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        onClick={() => setConfirmOpen(false)}
                        className="rounded-md border border-neutral-700 bg-transparent px-3 py-1.5 text-[12px] text-neutral-300 hover:bg-white/5"
                        disabled={regenLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRegenerateKey}
                        disabled={regenLoading}
                        className="inline-flex items-center gap-2 rounded-md bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-60"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${regenLoading ? "animate-spin" : ""}`} />
                        {regenLoading ? "Regenerating…" : "Regenerate"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Danger zone — pinned at the bottom of the card, always
                  visible regardless of which tab is selected. Hairline divider
                  separates it from tab content; rose tint so the action's
                  irreversibility is obvious without being noisy. */}
              <div className="mt-auto border-t border-red-900/40 bg-red-950/20 px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-red-100 uppercase tracking-wider">Danger zone</div>
                    <div className="text-[11px] text-neutral-400 truncate">
                      Permanently delete your account. This cannot be undone.
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-[12px] font-medium text-red-200 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete account
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ===== Delete account modal ===== */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={closeDeleteModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative z-[101] w-full max-w-lg rounded-md border border-red-900/40 bg-[#0a1224]/95 p-5 shadow-[0_0_40px_-10px_rgba(244,63,94,0.45)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-red-900/30">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <h3 id="delete-account-title" className="text-base font-semibold text-red-50">
                Delete account
              </h3>
            </div>

            {deletePrecheckLoading && (
              <div className="flex items-center gap-2 py-6 text-sm text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking account status…
              </div>
            )}

            {deletePrecheckError && !deletePrecheckLoading && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {deletePrecheckError}
              </div>
            )}

            {deleteSuccess && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-300 shrink-0" />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-emerald-100">{deleteSuccess}</div>
                  <div className="mt-1 text-[12px] text-neutral-400">Signing you out…</div>
                </div>
              </div>
            )}

            {!deleteSuccess && deletePrecheck && !deletePrecheckLoading && !deletePrecheckError && (
              deletePrecheck.can_delete === false ? (
                // Server-side block (e.g. user has an active paid plan).
                // Show the message verbatim + a single CTA to Contact, no
                // password/confirm fields.
                <div className="space-y-4">
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-300 shrink-0" />
                    <div className="text-[13px] text-amber-100 leading-relaxed">
                      {deletePrecheck.message || "Your account cannot be deleted right now. Please contact support."}
                    </div>
                  </div>
                </div>
              ) : (
                // Eligible: show the confirm flow.
                <div className="space-y-4">
                  <p className="text-sm text-neutral-300">
                    This will permanently remove your Whiteintel account, API key, saved searches, and login history. <span className="text-red-300">This cannot be undone.</span>
                  </p>

                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">Current password</label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="w-full rounded-md border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-sm outline-none focus:border-sky-500/50"
                      autoComplete="current-password"
                      disabled={deleteLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-neutral-300 mb-1">
                      Type <span className="font-mono font-semibold text-red-300">DELETE</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full rounded-md border border-red-900/40 bg-neutral-900/60 px-3 py-2 text-sm outline-none focus:border-red-500/50"
                      placeholder="DELETE"
                      disabled={deleteLoading}
                    />
                  </div>

                  {deleteError && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                      {deleteError}
                    </div>
                  )}
                </div>
              )
            )}

            {!deleteSuccess && (
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5 disabled:opacity-60"
              >
                {deletePrecheck?.can_delete === false ? "Close" : "Cancel"}
              </button>

              {/* Contact CTA — only when the user can't self-delete. Routes
                  to the in-app Contact page so support@ gets the conversation. */}
              {deletePrecheck?.can_delete === false && (
                <button
                  type="button"
                  onClick={() => {
                    closeDeleteModal();
                    navigate("/contact");
                    
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-2 text-sm font-semibold text-white transition-colors"
                >
                  Contact support
                </button>
              )}

              {/* Confirm delete — only when the user can self-delete. */}
              {deletePrecheck && deletePrecheck.can_delete !== false && (
                <button
                  onClick={handleDeleteAccount}
                  disabled={
                    deleteLoading ||
                    !deletePrecheck ||
                    deletePrecheckLoading ||
                    !!deletePrecheckError ||
                    !deletePassword ||
                    deleteConfirmText !== "DELETE"
                  }
                  className="inline-flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/20 px-3 py-2 text-sm text-red-100 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete account
                    </>
                  )}
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
